import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../Config/axiosInstance";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { Stack, IconButton, Typography, Alert, Snackbar } from "@mui/material";

import "./Category.css";

const AddCategory = () => {
  const navigate = useNavigate();
  const [categoryTitle, setCategoryTitle] = useState("");
  const [subcategories, setSubcategories] = useState([{ title: "", isSaved: false }]);
  const [showSubcategories, setShowSubcategories] = useState(false);
  const [confirmDeleteIndex, setConfirmDeleteIndex] = useState(null);

  //state-uri pentru validare si notificari
  const [titleError, setTitleError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false, 
    message: "",
    severity: "error"
  });

  //functie pentru verificarea existentei titlului - doar la submit
  const checkTitleExists = async(title) => {
    if(!title.trim()) {
      return false;
    } 

    try {
      const response = await axiosInstance.get("/category/check-title/", {
        params: {title: title.trim()}
      });

      return response.data.exists;
    } catch (error) {
      console.error("Error checking title:", error);
      return false;
    }
  };

  const handleCategoryChange = (e) => {
    const value = e.target.value;
    setCategoryTitle(value);
    //reseteaza eroarea la modificare titlului
    if (titleError) {
      setTitleError("");
    }
  };

  const handleSubcategoryChange = (index, value) => {
    const updated = [...subcategories];
    updated[index].title = value;
    setSubcategories(updated);
  };

  const handleAddSubcategory = () => {
    if (!showSubcategories) {
      setShowSubcategories(true);
      setSubcategories([{ title: "", isSaved: false}]);
    } else {
      setSubcategories([...subcategories, { title: "", isSaved: false }]);
    }
  };

  const handleSaveSubcategory = (index) => {
    if(!subcategories[index].title.trim()) {
      //afiseaza mesaj de eroare
      setSnackbar({
        open: true, 
        message: "Subcategory title cannot be empty.",
        severity: "error"
      });
      return;
    }

    //daca titlul este valid, salveaza subcategoria
    const updated = [...subcategories];
    updated[index].isSaved = true;
    setSubcategories(updated);
  };

  const handleEditSubcategory = (index) => {
    const updated = [...subcategories];
    updated[index].isSaved = false;
    setSubcategories(updated);
  };

  const handleConfirmDelete = (index) => {
    setConfirmDeleteIndex(index);
  };

  const handleCancelDelete = () => {
    setConfirmDeleteIndex(null);
  };

  const handleDeleteConfirmed = () => {
    const updated = [...subcategories];
    updated.splice(confirmDeleteIndex, 1);
    setSubcategories(updated);
    setConfirmDeleteIndex(null);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({...snackbar, open: false});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    //prevenire submit-uri multiple
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    
    //verificare daca titlu exista deja in sistem
    try {
      const exists = await checkTitleExists(categoryTitle);
      if (exists) {
        setTitleError("A category with this name already exists. Please choose a different title.");
        setSnackbar({
          open: true,
          message: "A category with this name already exists. Please choose a different title.",
          severity: "error"
        });
        setIsSubmitting(false);
        return;
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Error checking category existence. Please try again.",
        severity: "error"
      });
      setIsSubmitting(false);
      return;
    }

    //creare categorie si subcategorie
    try {
      const categoryResponse = await axiosInstance.post("/category/", {
        title: categoryTitle,
      });

      const categoryId = categoryResponse.data.id;

      const subcatRequests = subcategories
        .filter((s) => s.title.trim() !== "")
        .map((s) =>
          axiosInstance.post("/subcategory/", {
            title: s.title,
            category: categoryId,
          })
        );

      if (subcatRequests.length > 0) {
        await Promise.all(subcatRequests);
      }

      //afiseaza mesaj de succes
      setSnackbar({
        open: true, 
        message: "Category created successfully!",
        severity: "success"
      })
      
      setTimeout(() => {
        navigate(-1);
      }, 1500);
    } catch (error) {
      console.error("Error creating category:", error.response?.data || error.message);

      //verifica daca eroarea este de la titlu duplicat
      if (error.response?.status === 400 && 
          error.response?.data?.title?.includes("already exists")) {
        setTitleError("The name of this category already exists in the database.");
        setSnackbar({
          open: true,
          message: "A category with this name already exists. Please choose a different title.",
          severity: "error"
        });
      } else {
        setSnackbar({
          open: true,
          message: "An error occurred. Please try again.",
          severity: "error"
        });
      }
    }
  };

  return (
    <div className="category-page">
      <div className="category-header-container">
        <div className="category-header">
          <ArrowBackIcon className="back-arrow" onClick={() => navigate(-1)} />
          <h2 className="add-category-title">Create category</h2>
        </div>
      </div>

      <div className="full-width-separator-container">
        <hr className="title-separator" />
      </div>

      <div className="add-category-container">
        <form onSubmit={handleSubmit} className="add-category-form">
          <div className="form-group">
            <label htmlFor="category-title" className="label">
              Title<span className="category-required-star">*</span>
            </label>
            <input
              id="category-title"
              type="text"
              className={`input ${titleError ? "input-error" : ""}`}
              value={categoryTitle}
              onChange={handleCategoryChange}
              required
            />
            {titleError && (
              <Typography 
                variant="caption" 
                color="error" 
                sx={{ mt: 0, display: "block" }}
              >
                {titleError}
              </Typography>
            )}
          </div>

          <h4>Subcategories (Types)</h4>
          {showSubcategories && subcategories.map((subcat, index) => (
            <div key={index} className="subcategory-row">
              {!subcat.isSaved ? (
                <div className="subcategory-flex">
                  <label className="label">
                    Title<span className="category-required-star">*</span>
                  </label>
                  <input
                    type="text"
                    className="input"
                    value={subcat.title}
                    onChange={(e) => handleSubcategoryChange(index, e.target.value)}
                    placeholder={`Subcategory ${index + 1}`}
                  />
                  <div className="subcategory-actions">
                    <button
                      type="button"
                      className="button save-subcat"
                      onClick={() => handleSaveSubcategory(index)}
                    >
                      <span className="icon-desktop">Save</span>
                      <CheckIcon className="icon-mobile" />
                    </button>

                    <button
                      type="button"
                      className="button delete-subcat"
                      onClick={() => handleConfirmDelete(index)}
                    >
                      <span className="icon-desktop">Delete</span>
                      <CloseIcon className="icon-mobile" />
                    </button>
                  </div>
                </div>
              ) : (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    p: "4px 8px",
                    borderRadius: 1,
                    "&:hover": { backgroundColor: "action.hover" },
                  }}
                >
                  <FiberManualRecordIcon sx={{ fontSize: 12, color: "gray" }} />
                  <Typography variant="body2" className="subcategory-title">
                    {subcat.title}
                  </Typography>
                  <IconButton size="small" onClick={() => handleEditSubcategory(index)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Stack>
              )}
            </div>
          ))}

          <Button
            type="button"
            variant="contained"
            className="add-subcat-button"
            onClick={handleAddSubcategory}
            startIcon={<AddIcon />}
          >
            {showSubcategories ? "Add another subcategory" : "Add subcategories"}
          </Button>

          <div className="save-button-right">
            <button 
              type="submit" 
              className="button-save"
              disabled={isSubmitting} //dezactivare buton Save dupa click pentru a preveni crearile multiple
            >
              {isSubmitting ? "Saving..." : "Save"}
            </button>
          </div>
        </form>

        <Dialog open={confirmDeleteIndex !== null} onClose={handleCancelDelete}>
          <DialogTitle>Delete Confirmation</DialogTitle>
          <DialogContent>
            Are you sure you want to delete this subcategory?
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDelete} color="primary">Cancel</Button>
            <Button onClick={handleDeleteConfirmed} color="error">Delete</Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
    </div>
  );
};

export default AddCategory;